import React, { Fragment, PropsWithChildren, useState } from "react";
import styled from "styled-components";
import { Code } from "../Code";
import { ExpanderContent, ExpanderToggle } from "../Expander";

/**
 * `display: block` lets a wide table scroll horizontally, but it also drops the
 * table out of table layout: the rows form an anonymous table box that
 * shrink-wraps its content, so the table stops short of the container and
 * leaves dead space on the right. Light tables therefore lay out as a real
 * table at full width; the dark pages keep the scrolling behaviour they have
 * always had.
 */
const BorderlessTable = styled.table<{
  $fullWidth?: boolean;
}>`
  display: ${(props) => (props.$fullWidth ? "table" : "block")};
  ${(props) => (props.$fullWidth ? "width: 100%;" : "")}
  thead tr th {
    border-top: none !important;
  }
  ${/* Vertical only — row heights vary; horizontal stays left. */ ""}
  ${(props) => (props.$fullWidth ? `th, td { vertical-align: middle; }` : "")}
`;

interface Property<T> {
  title: string;
  render: (d: T) => React.ReactNode;
  hide?: boolean;
  style?: React.CSSProperties;
}

interface Props<T> {
  objects: T[];
  properties: Property<T>[];
  /**
   * Renders on a light surface instead of the app's default `table-dark`.
   * Opt-in so existing callers are untouched — the Content module is the only
   * light-themed area today.
   */
  light?: boolean;
  /**
   * Replaces the raw-document expander with a purpose-built detail view.
   * Defaults to the JSON dump every other page relies on.
   */
  renderExpanded?: (d: T) => React.ReactNode;
}

interface RowProps<T> {
  d: T;
  properties: Property<T>[];
  renderExpanded?: (d: T) => React.ReactNode;
}

function Row<T extends { _id: string }>({
  d,
  properties,
  renderExpanded,
}: RowProps<T>) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Fragment key={d._id}>
      <tr key={d._id}>
        <td>
          <ExpanderToggle
            expanded={expanded}
            label=" "
            toggle={() => setExpanded(!expanded)}
          />
        </td>
        {properties
          .filter((p) => !p.hide)
          .map((p, i) => {
            return <td key={i}>{p.render(d)}</td>;
          })}
      </tr>
      <ExpanderContent expanded={expanded} key={`x+${d._id}`}>
        <tr key={`x+${d._id}`}>
          <td colSpan={properties.length + 1}>
            {renderExpanded ? renderExpanded(d) : <Code object={d} />}
          </td>
        </tr>
      </ExpanderContent>
    </Fragment>
  );
}

export function DocumentTable<T extends { _id: string }>({
  objects,
  properties,
  light,
  renderExpanded,
}: PropsWithChildren<Props<T>>) {
  return (
    <BorderlessTable
      $fullWidth={light}
      className={`table ${
        light ? "" : "table-dark "
      }table-sm table-hover overflow-auto`}
    >
      <colgroup>
        {properties
          .filter((p) => !p.hide)
          .map((p, i) => (
            <col key={i} style={p.style} />
          ))}
      </colgroup>
      <thead>
        <tr className="borderless">
          <th />
          {properties
            .filter((p) => !p.hide)
            .map((p, i) => (
              <th key={i}>{p.title}</th>
            ))}
        </tr>
      </thead>
      <tbody>
        {objects.map((d) => (
          <Row
            properties={properties}
            d={d}
            key={d._id}
            renderExpanded={renderExpanded}
          />
        ))}
      </tbody>
    </BorderlessTable>
  );
}

export type DocumentTableProperty<T> = Property<T>;
